import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFilters } from '../context/FilterContext';

/**
 * useFetchData  –  Centralized data fetching hook.
 *
 * RULES:
 * 1. If no filters are active → returns null immediately (no loading, no API call)
 * 2. If filters are active → calls fetchFn(filters) after a short debounce
 *    so that cascading filter resets settle before triggering API calls.
 *
 * @param {Function} fetchFn  –  API function (e.g. getSalesSummary)
 */
const DEBOUNCE_MS = 300;

const useFetchData = (fetchFn) => {
  const { filters, hasActiveFilters, latestYearmonth } = useFilters();

  const effectiveFilters = useMemo(
    () => ({ ...filters, latestYearmonth }),
    [filters, latestYearmonth],
  );

  // Stable string key so the effect only fires when filter VALUES change,
  // not on every new object reference.
  const filterKey = useMemo(
    () => JSON.stringify(effectiveFilters),
    [effectiveFilters],
  );

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Keep a stable ref to fetchFn so we don't need it in the dep array
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // Keep a ref to the latest effectiveFilters so the debounced call uses current values
  const filtersRef = useRef(effectiveFilters);
  filtersRef.current = effectiveFilters;

  useEffect(() => {
    // ── GATE: no filters selected → do nothing ──
    if (!hasActiveFilters) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    // Debounce: wait for cascading filter resets to settle
    const timer = setTimeout(() => {
      if (cancelled) return;

      setLoading(true);
      setError(null);

      fetchFnRef.current(filtersRef.current)
        .then(result => {
          if (!cancelled) setData(result);
        })
        .catch(err => {
          console.error('[useFetchData] error:', err);
          if (!cancelled) setError(err?.message || 'Failed to fetch data');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filterKey, hasActiveFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    if (!hasActiveFilters) return;
    setLoading(true);
    setError(null);
    fetchFnRef.current(filtersRef.current)
      .then(result => setData(result))
      .catch(err => setError(err?.message || 'Failed to fetch data'))
      .finally(() => setLoading(false));
  }, [filterKey, hasActiveFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, refetch };
};

export default useFetchData;
